import { useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  getAllPartiesByDistributionV2,
  type GetAllPartiesByDistributionV2Response,
} from 'wg-api-sdk';

import wgApi from '@/lib/api/wgApi';
import { applyDemoPartyOverridesToList } from '@/lib/party/demoPartyOverrides';
import useCurrentBroker from '@/queries/hooks/useCurrentBroker';
import type { QueryOptions } from '@/types/react-query';

import queryKeys from '../queryKeys';

export default function useParties(
  distributionId: string,
  filter?: {
    query?: string;
    emails?: string[];
  },
  options?: QueryOptions<GetAllPartiesByDistributionV2Response['items']>,
) {
  const { data: broker } = useCurrentBroker();
  const optionsSelect = options?.select;

  const select = useCallback(
    (parties: GetAllPartiesByDistributionV2Response['items']) => {
      const overridden = applyDemoPartyOverridesToList(parties, broker?.email);

      return optionsSelect ? optionsSelect(overridden) : overridden;
    },
    [broker?.email, optionsSelect],
  );

  return useQuery({
    queryKey: queryKeys.list(distributionId, filter),
    queryFn: async () => {
      const data = await getAllPartiesByDistributionV2(
        {
          path: { distribution_id: distributionId },
          query: { q: filter?.query ?? undefined, emails: filter?.emails },
        },
        wgApi,
      );

      return data.items;
    },
    ...options,
    select,
  });
}
